import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Menu as MantineMenu } from '@mantine/core';
import clsx from 'clsx';

import { Button } from '../buttons/Button';
import { GoogleFontIcon } from '../icons/GoogleFontIcon';
import { Menu } from '../menu/Menu';

import type { PdfFitMode, PdfZoomDirection } from './pdfViewerScale';
import { useToolbarCollapse } from './useToolbarCollapse';

import styles from './PdfPreviewToolbar.module.scss';

type PdfPreviewToolbarProps = {
  currentPage: number;
  isPagesOpen: boolean;
  numPages: number;
  scalePct: number;
  isHandTool: boolean;
  isHorizontal: boolean;
  onTogglePages: () => void;
  onPageCommit: (pageNumber: number) => void;
  onZoom: (direction: PdfZoomDirection) => void;
  onFit: (mode: Exclude<PdfFitMode, null>) => void;
  onToggleHandTool: () => void;
  onRotate: () => void;
  onToggleScrollMode: () => void;
  onToggleSearch: () => void;
  onOpenSearch: () => void;
  onPrint: () => void;
  onDownload: () => void;
};

export function PdfPreviewToolbar({
  currentPage,
  isPagesOpen,
  numPages,
  scalePct,
  isHandTool,
  isHorizontal,
  onTogglePages,
  onPageCommit,
  onZoom,
  onFit,
  onToggleHandTool,
  onRotate,
  onToggleScrollMode,
  onToggleSearch,
  onOpenSearch,
  onPrint,
  onDownload,
}: PdfPreviewToolbarProps) {
  const { t } = useTranslation();
  const toolbarRef = useRef<HTMLDivElement>(null);
  const [pageInput, setPageInput] = useState(String(currentPage));
  const isCollapsed = useToolbarCollapse({
    toolbarRef,
    currentPage,
    isHorizontal,
    isPagesOpen,
    numPages,
    scalePct,
  });

  useEffect(() => {
    setPageInput(String(currentPage));
  }, [currentPage]);

  function commitPageInput() {
    const pageNumber = Number(pageInput);

    if (
      Number.isInteger(pageNumber) &&
      pageNumber >= 1 &&
      pageNumber <= numPages
    ) {
      onPageCommit(pageNumber);
    } else {
      setPageInput(String(currentPage));
    }
  }

  return (
    <div
      className={styles['toolbar']}
      data-testid="pdf-document-preview-toolbar"
      ref={toolbarRef}
    >
      <Button
        data-testid="pdf-document-preview-pages-toggle"
        icon={
          <GoogleFontIcon
            className={styles['pages-toggle-icon']}
            name="view_sidebar"
            solid={isPagesOpen}
          />
        }
        onClick={onTogglePages}
        title={
          isPagesOpen
            ? t('app.pdfDocumentPreview.hidePages')
            : t('app.pdfDocumentPreview.showPages')
        }
        variant="text"
      />
      <span className={styles['divider']} />
      <input
        aria-label={t('app.pdfDocumentPreview.currentPage')}
        className={styles['page-input']}
        onBlur={commitPageInput}
        onChange={(event) => setPageInput(event.target.value)}
        onKeyDown={(event) => event.key === 'Enter' && commitPageInput()}
        value={pageInput}
      />
      <span className={styles['quiet']}>/ {numPages || '–'}</span>
      <span className={styles['divider']} />
      <Button
        icon={<GoogleFontIcon name="remove" solid />}
        onClick={() => onZoom('out')}
        title={t('app.pdfDocumentPreview.zoomOut')}
        variant="text"
      />
      <span className={styles['zoom']}>{scalePct}%</span>
      <Button
        icon={<GoogleFontIcon name="add" solid />}
        onClick={() => onZoom('in')}
        title={t('app.pdfDocumentPreview.zoomIn')}
        variant="text"
      />
      <Button
        icon={<GoogleFontIcon name="fit_width" solid />}
        onClick={() => onFit('page-width')}
        title={t('app.pdfDocumentPreview.fitWidth')}
        variant="text"
      />
      <Button
        icon={<GoogleFontIcon name="fit_page_height" solid />}
        onClick={() => onFit('page-fit')}
        title={t('app.pdfDocumentPreview.fitPage')}
        variant="text"
      />
      <span className={styles['divider']} />
      <span
        className={clsx(styles['toolbar-collapsible'], {
          [styles['toolbar-collapsible--hidden']]: isCollapsed,
        })}
      >
        <Button
          icon={<GoogleFontIcon name="pan_tool" solid={isHandTool} />}
          onClick={onToggleHandTool}
          title={
            isHandTool
              ? t('app.pdfDocumentPreview.disableHandTool')
              : t('app.pdfDocumentPreview.handTool')
          }
          variant="text"
        />
        <Button
          icon={<GoogleFontIcon name="rotate_right" solid />}
          onClick={onRotate}
          title={t('app.pdfDocumentPreview.rotate')}
          variant="text"
        />
        <Button
          icon={
            <GoogleFontIcon
              name={isHorizontal ? 'view_day' : 'view_column'}
              solid
            />
          }
          onClick={onToggleScrollMode}
          title={
            isHorizontal
              ? t('app.pdfDocumentPreview.verticalLayout')
              : t('app.pdfDocumentPreview.horizontalLayout')
          }
          variant="text"
        />
        <Button
          icon={<GoogleFontIcon name="search" solid />}
          onClick={onToggleSearch}
          title={t('app.pdfDocumentPreview.searchTitle')}
          variant="text"
        />
        <Button
          icon={<GoogleFontIcon name="print" solid />}
          onClick={onPrint}
          title={t('app.pdfDocumentPreview.print')}
          variant="text"
        />
        <Button
          icon={<GoogleFontIcon name="download" solid />}
          onClick={onDownload}
          title={t('app.pdfDocumentPreview.download')}
          variant="text"
        />
      </span>
      {isCollapsed ? (
        <Menu
          menuDropdown={
            <>
              <MantineMenu.Item
                leftSection={
                  <GoogleFontIcon name="pan_tool" solid={isHandTool} />
                }
                onClick={onToggleHandTool}
              >
                {isHandTool
                  ? t('app.pdfDocumentPreview.disableHandTool')
                  : t('app.pdfDocumentPreview.handTool')}
              </MantineMenu.Item>
              <MantineMenu.Item
                leftSection={<GoogleFontIcon name="rotate_right" solid />}
                onClick={onRotate}
              >
                {t('app.pdfDocumentPreview.rotate')}
              </MantineMenu.Item>
              <MantineMenu.Item
                leftSection={
                  <GoogleFontIcon
                    name={isHorizontal ? 'view_day' : 'view_column'}
                    solid
                  />
                }
                onClick={onToggleScrollMode}
              >
                {isHorizontal
                  ? t('app.pdfDocumentPreview.verticalLayout')
                  : t('app.pdfDocumentPreview.horizontalLayout')}
              </MantineMenu.Item>
              <MantineMenu.Item
                leftSection={<GoogleFontIcon name="search" solid />}
                onClick={onOpenSearch}
              >
                {t('app.pdfDocumentPreview.search')}
              </MantineMenu.Item>
              <MantineMenu.Item
                leftSection={<GoogleFontIcon name="print" solid />}
                onClick={onPrint}
              >
                {t('app.pdfDocumentPreview.print')}
              </MantineMenu.Item>
              <MantineMenu.Item
                leftSection={<GoogleFontIcon name="download" solid />}
                onClick={onDownload}
              >
                {t('app.pdfDocumentPreview.download')}
              </MantineMenu.Item>
            </>
          }
          menuTarget={
            <Button
              icon={<GoogleFontIcon name="more_vert" solid />}
              title={t('app.pdfDocumentPreview.moreTools')}
              variant="text"
            />
          }
          position="bottom-end"
          width={200}
        />
      ) : null}
    </div>
  );
}
